import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

export interface PmPlanDto {
  id: string;
  company_id: string;
  asset_id: string;
  title: string;
  frequency_unit: 'days' | 'weeks' | 'months' | 'years';
  frequency_value: number;
  start_date: string | null;
  next_due_date: string | null;
  last_completed_at: string | null;
  checklist_json: string[] | null;
  is_active: boolean;
  remarks: string | null;
  created_at?: string;
}

export interface CaseDto {
  id: string;
  company_id: string;
  asset_id: string;
  case_number: string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  reported_by: string | null;
  assigned_employee_id: string | null;
  labor_cost: number | null;
  material_cost: number | null;
  other_cost: number | null;
  reported_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  resolution_notes: string | null;
  created_at?: string;
}

export interface AmcDto {
  id: string;
  company_id: string;
  asset_id: string;
  supplier_id: string | null;
  contract_number: string | null;
  start_date: string | null;
  end_date: string | null;
  response_sla_hours: number | null;
  is_active: boolean;
  notes: string | null;
  created_at?: string;
}

const assetMaintenanceService = {
  async listCases(params?: { asset_id?: string; status?: string }): Promise<CaseDto[]> {
    const res = await api.get('/asset-maintenance/cases', { params });
    const data = unwrap<CaseDto[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async getCase(id: string): Promise<CaseDto | null> {
    const rows = await this.listCases();
    return rows.find((r) => r.id === id) ?? null;
  },
  async createCase(body: Record<string, unknown>): Promise<CaseDto> {
    const res = await api.post('/asset-maintenance/cases', body);
    return unwrap(res);
  },
  async updateCase(id: string, body: Record<string, unknown>): Promise<CaseDto> {
    const res = await api.put(`/asset-maintenance/cases/${id}`, body);
    return unwrap(res);
  },
  async removeCase(id: string): Promise<void> {
    await api.delete(`/asset-maintenance/cases/${id}`);
  },

  async listPm(params?: { asset_id?: string }): Promise<PmPlanDto[]> {
    const res = await api.get('/asset-maintenance/pm-plans', { params });
    const data = unwrap<PmPlanDto[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async getPm(id: string): Promise<PmPlanDto | null> {
    const rows = await this.listPm();
    return rows.find((r) => r.id === id) ?? null;
  },
  async createPm(body: Record<string, unknown>): Promise<PmPlanDto> {
    const res = await api.post('/asset-maintenance/pm-plans', body);
    return unwrap(res);
  },
  async updatePm(id: string, body: Record<string, unknown>): Promise<PmPlanDto> {
    const res = await api.put(`/asset-maintenance/pm-plans/${id}`, body);
    return unwrap(res);
  },
  async completePm(id: string, body: Record<string, unknown>): Promise<PmPlanDto> {
    const res = await api.post(`/asset-maintenance/pm-plans/${id}/complete`, body);
    return unwrap(res);
  },
  async removePm(id: string): Promise<void> {
    await api.delete(`/asset-maintenance/pm-plans/${id}`);
  },

  async listAmc(params?: { asset_id?: string }): Promise<AmcDto[]> {
    const res = await api.get('/asset-maintenance/amc', { params });
    const data = unwrap<AmcDto[]>(res);
    return Array.isArray(data) ? data : [];
  },
  async getAmc(id: string): Promise<AmcDto | null> {
    const rows = await this.listAmc();
    return rows.find((r) => r.id === id) ?? null;
  },
  async createAmc(body: Record<string, unknown>): Promise<AmcDto> {
    const res = await api.post('/asset-maintenance/amc', body);
    return unwrap(res);
  },
  async updateAmc(id: string, body: Record<string, unknown>): Promise<AmcDto> {
    const res = await api.put(`/asset-maintenance/amc/${id}`, body);
    return unwrap(res);
  },
  async removeAmc(id: string): Promise<void> {
    await api.delete(`/asset-maintenance/amc/${id}`);
  },
};

export default assetMaintenanceService;
